import styled, { css } from 'styled-components';

/**
 * Form components for consistent form styling
 * Usage:
 * <FormGroup>
 *   <FormLabel htmlFor="email">Email</FormLabel>
 *   <FormInput type="email" id="email" />
 *   <FormError>Please enter a valid email</FormError>
 * </FormGroup>
 */

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333333;
  font-size: 0.9rem;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  color: #333333;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
  
  ${props => props.error && css`
    border-color: #DB4437;
    
    &:focus {
      box-shadow: 0 0 0 2px rgba(219, 68, 55, 0.2);
    }
  `}
  
  ${props => props.disabled && css`
    background-color: #f5f5f5;
    cursor: not-allowed;
  `}
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.9rem;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  color: #333333;
  transition: all 0.3s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }
  
  option {
    background-color: #ffffff;
    color: #333333;
  }
  
  ${props => props.error && css`
    border-color: #DB4437;
    
    &:focus {
      box-shadow: 0 0 0 2px rgba(219, 68, 55, 0.2);
    }
  `}
  
  ${props => props.disabled && css`
    background-color: #f5f5f5;
    cursor: not-allowed;
  `}
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.9rem;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  color: #333333;
  transition: all 0.3s;
  min-height: ${props => props.rows ? `${props.rows * 1.5}rem` : '6rem'};
  resize: ${props => props.resize || 'vertical'};
  
  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
  
  ${props => props.error && css`
    border-color: #DB4437;
    
    &:focus {
      box-shadow: 0 0 0 2px rgba(219, 68, 55, 0.2);
    }
  `}
  
  ${props => props.disabled && css`
    background-color: #f5f5f5;
    cursor: not-allowed;
  `}
`;

export const FormError = styled.div`
  color: #DB4437;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-weight: 500;
`;

export const FormHint = styled.div`
  color: #5F6368;
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

export const FormCheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

export const FormCheckbox = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 0.5rem;
  cursor: pointer;
`;

export const FormRadioGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FormRadio = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  
  input[type="radio"] {
    margin-right: 0.5rem;
    cursor: pointer;
  }
  
  label {
    cursor: pointer;
  }
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: ${props => props.align || 'flex-end'};
  gap: 1rem;
  margin-top: 2rem;
`;

export const FormDivider = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin: 1.5rem 0;
`;

export const FormFileInput = styled.div`
  position: relative;
  
  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  
  .file-input-label {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.9rem;
    background-color: #f5f5f5;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    color: #5F6368;
    transition: all 0.3s;
    
    &:hover {
      background-color: #e8f0fe;
      border-color: #4285F4;
    }
  }
  
  ${props => props.error && css`
    .file-input-label {
      border-color: #DB4437;
      background-color: rgba(219, 68, 55, 0.05);
    }
  `}
`;

export const FormSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }
`;

const FormComponents = {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  FormError,
  FormCheckboxGroup,
  FormCheckbox,
  FormRadioGroup,
  FormRadio,
  FormHint,
  FormActions,
  FormDivider,
  FormFileInput,
  FormSection
};

export default FormComponents;
